import React from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { Checkbox } from "../../../components/ui/Checkbox";

const EmployeeListTable = ({
  employees = [],
  onEmployeeClick,
  selectedEmployees = [],
  onSelectionChange,
}) => {
  const isSelected = (employeeId) => selectedEmployees?.includes(employeeId);

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(employees?.map((e) => e?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectOne = (employeeId, checked) => {
    if (checked) {
      onSelectionChange([...selectedEmployees, employeeId]);
    } else {
      onSelectionChange(selectedEmployees?.filter((id) => id !== employeeId));
    }
  };

  const getEligibilityBadge = (status) => {
    const badges = {
      Eligible: {
        color: "var(--color-success)",
        bg: "rgba(34, 197, 94, 0.1)",
        text: "Eligible",
      },
      "Not Eligible": {
        color: "var(--color-error)",
        bg: "rgba(239, 68, 68, 0.1)",
        text: "Not Eligible",
      },
      Probationary: {
        color: "var(--color-accent)",
        bg: "rgba(251, 146, 60, 0.1)",
        text: "Probationary",
      },
    };
    return badges?.[status] || badges?.["Not Eligible"];
  };

  const allSelected =
    employees?.length > 0 && selectedEmployees?.length === employees?.length;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left">
                <Checkbox
                  checked={allSelected}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Employee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Employment Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Service Hours
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Eligibility
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {employees?.length === 0 ? (
              <tr>
                <td
                  colSpan="7"
                  className="px-6 py-12 text-center text-gray-500"
                >
                  <Icon
                    name="Users"
                    size={48}
                    className="mx-auto mb-3 opacity-30"
                  />
                  <p className="text-lg font-medium">No employees found</p>
                  <p className="text-sm">
                    Try adjusting your filters or add a new employee
                  </p>
                </td>
              </tr>
            ) : (
              employees?.map((employee) => {
                const badge = getEligibilityBadge(employee?.eligibilityStatus);
                return (
                  <tr
                    key={employee?.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => onEmployeeClick(employee)}
                  >
                    <td
                      className="px-6 py-4"
                      onClick={(e) => e?.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected(employee?.id)}
                        onChange={(e) =>
                          handleSelectOne(employee?.id, e?.target?.checked)
                        }
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={employee?.photo}
                          alt={`${employee?.name} profile photo`}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <div className="font-medium text-gray-900">
                            {employee?.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {employee?.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">
                        {employee?.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900">
                        {employee?.employmentType}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Icon
                          name="Clock"
                          size={16}
                          color="var(--color-muted-foreground)"
                        />
                        <span className="text-sm font-medium text-gray-900">
                          {employee?.serviceHours}h
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          color: badge?.color,
                          backgroundColor: badge?.bg,
                        }}
                      >
                        {badge?.text}
                      </span>
                    </td>
                    <td
                      className="px-6 py-4 text-right"
                      onClick={(e) => e?.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Eye"
                          onClick={() => onEmployeeClick(employee)}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Edit"
                          onClick={() => onEmployeeClick(employee)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmployeeListTable;
